import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Page } from 'shared/components/Page'
import { useAdminTrpc } from 'shared/api'
import { Button } from 'shared/components/ui/button'
import { Input } from 'shared/components/ui/input'
import { formatUzsAmount } from 'shared/lib/format'
import { useTranslate } from 'shared/lib/i18n'
import { RejectDialog } from './RejectDialog'

export function TopupsQueue() {
	const trpc = useAdminTrpc()
	const queryClient = useQueryClient()
	const [amounts, setAmounts] = useState<Record<string, string>>({})
	const { t, prefixT } = useTranslate('pages.adminTopups')

	const { data, isLoading } = useQuery(trpc.topups.ListPending.queryOptions({}))
	const confirm = useMutation(trpc.topups.Confirm.mutationOptions())
	const reject = useMutation(trpc.topups.Reject.mutationOptions())

	async function handleConfirm(id: string, declared: number) {
		const corrected = Number((amounts[id] ?? '').replace(/\D/g, '')) || undefined
		try {
			const res = await confirm.mutateAsync({
				topupId: id,
				confirmedAmountUzs: corrected !== declared ? corrected : undefined,
			})
			toast.success(prefixT('confirmed', { balance: t('common.amountUzs', { amount: formatUzsAmount(res.balanceAfterUzs) }) }))
		} catch (e) {
			toast.error(e instanceof Error && e.message.includes('TOPUP_ALREADY_HANDLED')
				? prefixT('alreadyHandled')
				: prefixT('confirmFailed'))
		}
		void queryClient.invalidateQueries()
	}

	async function handleReject(id: string, reason: string) {
		try {
			await reject.mutateAsync({ topupId: id, reason: reason || undefined })
			toast.success(prefixT('rejected'))
		} catch {
			toast.error(prefixT('rejectFailed'))
		}
		void queryClient.invalidateQueries()
	}

	return (
		<Page>
			<div className="p-4">
				<h1 className="mb-4 text-xl font-semibold text-foreground">
					{data ? prefixT('titleWithCount', { count: String(data.total) }) : prefixT('title')}
				</h1>
				{isLoading && <div className="py-8 text-center">{t('common.loading')}</div>}
				{data?.items.length === 0 && (
					<div className="py-8 text-center text-muted-foreground">{prefixT('empty')}</div>
				)}
				<div className="space-y-3">
					{data?.items.map((topup) => (
						<div key={topup._id} className="rounded-xl border p-3">
							<div className="flex gap-3">
								{topup.screenshotUrl && (
									<a href={topup.screenshotUrl} target="_blank" rel="noreferrer">
										<img
											src={topup.screenshotUrl}
											alt={prefixT('receiptAlt')}
											className="h-20 w-20 rounded-lg object-cover"
										/>
									</a>
								)}
								<div className="min-w-0 flex-1">
									<div className="font-semibold">{t('common.amountUzs', { amount: formatUzsAmount(topup.amountUzs) })}</div>
									<div className="truncate text-sm text-muted-foreground">
										<Link className="underline" to={`/admin/clients/${topup.buyerUserId}`}>
											{topup.buyerTezId ?? topup.buyerName}
										</Link>
									</div>
									<div className="text-xs text-muted-foreground">
										{new Date(topup.createdAt).toLocaleString()}
									</div>
								</div>
							</div>
							<div className="mt-2 flex items-center gap-2">
								<Input
									className="w-36"
									inputMode="numeric"
									placeholder={t('common.amountUzs', { amount: formatUzsAmount(topup.amountUzs) })}
									value={amounts[topup._id] ?? ''}
									onChange={(e) => setAmounts((m) => ({ ...m, [topup._id]: e.target.value }))}
								/>
								<Button
									size="sm"
									disabled={confirm.isPending}
									onClick={() => handleConfirm(topup._id, topup.amountUzs)}
								>
									{t('common.confirm')}
								</Button>
								<RejectDialog
									pending={reject.isPending}
									onReject={(reason) => handleReject(topup._id, reason)}
								/>
							</div>
						</div>
					))}
				</div>
			</div>
		</Page>
	)
}
